import { ConditionalRuleData, ConditionalRuleSet } from "domain/conditionalFormattingRules";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { remove, RepositoryKey, setObject } from "utils/repository";

const CONDITIONAL_RULES_PATH = "/api/v1/conditional-rules";

interface ConditionalRuleDto {
    color: string;
    value: string;
    enabled: boolean;
    operator: string;
    order?: number;
    priority?: number;
}

interface ConditionalRuleResponseDto {
    tenant_uuid: string;
    rule_uuid: string;
    rule_set: Record<string, ConditionalRuleDto[]>;
    updated_by?: string;
    created_date?: string;
    updated_date?: string;
}

interface FetchConditionalRulesResponseDto {
    conditional_rules: ConditionalRuleResponseDto[];
}

interface CreateConditionalRuleResponseDto {
    conditional_rule: { rule_uuid: string };
}

interface ConditionalRuleSetDto {
    rule_set: Record<string, ConditionalRuleDto[]>;
}

function toConditionalRuleData(dto: ConditionalRuleResponseDto): ConditionalRuleData {
    return {
        tenantUuid: dto.tenant_uuid,
        ruleUuid: dto.rule_uuid,
        ruleSet: dto.rule_set,
        updatedBy: dto.updated_by,
        createdDate: dto.created_date,
        updatedDate: dto.updated_date,
    };
}

function toConditionalRuleSetDto(ruleSet: ConditionalRuleSet): ConditionalRuleSetDto {
    return { rule_set: ruleSet.ruleSet };
}

class ConditionalRulesService {
    public async fetchRules(abortController?: AbortController): Promise<ConditionalRuleData[]> {
        const response = await apiGatewayService.invokeApi<null, FetchConditionalRulesResponseDto>(
            CONDITIONAL_RULES_PATH,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
            }
        );
        const rules = response.conditional_rules.map(toConditionalRuleData);
        setObject(RepositoryKey.CONDITIONAL_RULES, rules);
        return rules;
    }

    public async createRule(ruleSet: ConditionalRuleSet, abortController?: AbortController): Promise<string> {
        const response = await apiGatewayService.invokeApi<ConditionalRuleSetDto, CreateConditionalRuleResponseDto>(
            CONDITIONAL_RULES_PATH,
            "POST",
            toConditionalRuleSetDto(ruleSet),
            {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
            }
        );
        await this.refreshLocalStorage();
        return response.conditional_rule.rule_uuid;
    }

    public async updateRule(
        ruleUuid: string,
        ruleSet: ConditionalRuleSet,
        abortController?: AbortController
    ): Promise<void> {
        await apiGatewayService.invokeApi(
            `${CONDITIONAL_RULES_PATH}/${ruleUuid}`,
            "POST",
            toConditionalRuleSetDto(ruleSet),
            {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
            }
        );
        await this.refreshLocalStorage();
    }

    public async deleteRule(ruleUuid: string, abortController?: AbortController): Promise<void> {
        await apiGatewayService.invokeApi(`${CONDITIONAL_RULES_PATH}/${ruleUuid}`, "DELETE", null, {
            abortSignal: abortController?.signal,
            apiType: ApiType.ARTHUR,
        });
        remove(RepositoryKey.CONDITIONAL_RULES);
    }

    private async refreshLocalStorage(): Promise<void> {
        await this.fetchRules();
    }
}

export const conditionalRulesService = new ConditionalRulesService();
